import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter, StaticRouter } from 'react-router-dom';
import { Provider, useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import 'moment/locale/fi';

// Configs and store setup
import defaultConfig from './config/configDefault';
import appSettings from './config/settings';
import configureStore from './store';

// utils
import { RouteConfigurationProvider } from './context/routeConfigurationContext';
import { ConfigurationProvider } from './context/configurationContext';
import { LanguageProvider, useLanguage } from './context/languageContext';
import { parse } from './util/urlHelpers';
import { isEmpty } from './util/common';
import { mergeConfig } from './util/configHelpers';
import { IntlProvider } from './util/reactIntl';
import {
  clearReferralDataIfExpired,
  filterValidReferralData,
  storeReferralData,
} from './util/webStorageHelpers';
import { includeCSSProperties } from './util/style';
import { IncludeScripts } from './util/includeScripts';
import {
  getLanguageOptions,
  getUserLanguagePreference,
  languageFromLocale,
  localeForLanguage,
  readLanguageCookie,
  resolveLanguage,
  storeLanguageCookie,
} from './util/language';
import { mergeTranslationsWithLocalOverrides, mergeValidTranslations } from './util/translation';
import { localizeHostedContent } from './util/pageLocalization';
import { localizeMarketplaceConfig } from './util/marketplaceConfigLocalization';
import { updateLanguagePreference } from './ducks/user.duck';

import { MaintenanceMode } from './components';
import ConsumerIdentitySync from './components/ConsumerIdentitySync/ConsumerIdentitySync';

// routing
import routeConfiguration from './routing/routeConfiguration';
import Routes from './routing/Routes';

// Sharetribe Web Template uses English translations as default translations.
import defaultMessages from './translations/en.json';
import finnishMessages from './translations/fi.json';

// Console provides the hosted texts for one configured locale. Secondary
// languages are bundled here and use English as a missing-key fallback.

// Note that there is also translations in './translations/countryCodes.js' file
// This file contains ISO 3166-1 alpha-2 country codes, country names and their translations in our default languages
// This used to collect billing address in StripePaymentAddress on CheckoutPage

// Moment's Finnish locale is imported statically above. Keeping both supported
// locales in the main bundle avoids remounting the page when the user switches.

// The "./translations/en.json" has generic English translations
// that should work as a default translation if some translation keys are missing
// from the hosted translation.json (which can be edited in Console). The other files
// in that directory has the generic fallback translations.
//
// Invalid, missing, or argument-incompatible messages fall back to English.

const isTestEnv = process.env.NODE_ENV === 'test';
const bundledMessages = {
  en: defaultMessages,
  fi: finnishMessages,
};
const testMessages = Object.fromEntries(Object.entries(defaultMessages).map(([key]) => [key, key]));
const locallyOwnedMessageKeys = Object.keys(defaultMessages).filter(key =>
  key.includes('.default-negotiation.')
);

const messagesForLanguage = (language, hostedTranslations) => {
  if (isTestEnv) {
    return testMessages;
  }

  const messagesInLanguage = bundledMessages[language] || {};
  const messagesWithEnglishFallback = mergeValidTranslations(defaultMessages, messagesInLanguage);
  const isHostedLanguage = language === appSettings.hostedTranslationsLanguage;

  // Console supports one hosted marketplace-text language. Apply that asset only
  // to its configured locale; secondary languages remain bundled in the client.
  return isHostedLanguage
    ? mergeTranslationsWithLocalOverrides(
        messagesWithEnglishFallback,
        hostedTranslations,
        locallyOwnedMessageKeys
      )
    : messagesWithEnglishFallback;
};

const localizedConfig = (appConfig, preferredLanguage) => {
  const hostedLocale = appConfig.localization.locale;
  const language = isTestEnv ? 'en' : resolveLanguage(preferredLanguage, hostedLocale);
  const locale = isTestEnv ? 'en' : localeForLanguage(language, hostedLocale);
  const marketplaceConfig = localizeMarketplaceConfig(appConfig, language);

  return {
    ...marketplaceConfig,
    topbar: localizeHostedContent(appConfig.topbar, language, 'top-bar'),
    footer: localizeHostedContent(appConfig.footer, language, 'footer'),
    localization: {
      ...appConfig.localization,
      language,
      locale,
      availableLanguages: getLanguageOptions(hostedLocale),
    },
  };
};

const MomentLocaleLoader = props => {
  const { children, locale } = props;

  // EN and FI locale data are bundled up front so changing language keeps the
  // existing route tree mounted and preserves the user's scroll position.
  moment.locale(locale);
  return <>{children}</>;
};

const Configurations = props => {
  const { appConfig, children } = props;
  const routeConfig = routeConfiguration(appConfig.layout, appConfig?.accessControl);
  const locale = isTestEnv ? 'en' : appConfig.localization.locale;

  return (
    <ConfigurationProvider value={appConfig}>
      <MomentLocaleLoader locale={locale}>
        <RouteConfigurationProvider value={routeConfig}>{children}</RouteConfigurationProvider>
      </MomentLocaleLoader>
    </ConfigurationProvider>
  );
};

const MaintenanceModeError = props => {
  const { locale, messages, helmetContext } = props;
  return (
    <IntlProvider locale={locale} messages={messages} textComponent="span">
      <HelmetProvider context={helmetContext}>
        <MaintenanceMode />
      </HelmetProvider>
    </IntlProvider>
  );
};

// This displays a warning if environment variable key contains a string "SECRET"
const EnvironmentVariableWarning = props => {
  const suspiciousEnvKey = props.suspiciousEnvKey;
  // https://github.com/sharetribe/flex-integration-api-examples#warning-usage-with-your-web-app--website
  const containsINTEG = str => str.toUpperCase().includes('INTEG');
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
      }}
    >
      <div style={{ width: '600px' }}>
        <p>
          Are you sure you want to reveal to the public web an environment variable called:{' '}
          <b>{suspiciousEnvKey}</b>
        </p>
        <p>
          All the environment variables that start with <i>REACT_APP_</i> prefix will be part of the
          published React app that's running on a browser. Those variables are, therefore, visible
          to anyone on the web. Secrets should only be used on a secure environment like the server.
        </p>
        {containsINTEG(suspiciousEnvKey) ? (
          <p>
            {'Note: '}
            <span style={{ color: 'red' }}>
              Do not use Integration API directly from the web app.
            </span>
          </p>
        ) : null}
      </div>
    </div>
  );
};

const LanguagePreferenceSync = () => {
  const dispatch = useDispatch();
  const currentUser = useSelector(state => state.user.currentUser);
  const { language } = useLanguage();
  const savedLanguage = getUserLanguagePreference(currentUser);

  useEffect(() => {
    if (currentUser && savedLanguage !== language) {
      dispatch(updateLanguagePreference(language)).catch(() => {});
    }
  }, [currentUser, dispatch, language, savedLanguage]);

  return null;
};

/**
 * Client App
 * @param {Object} props
 * @param {Object} props.store
 * @param {Object} props.hostedTranslations
 * @param {Object} props.hostedConfig
 * @returns {JSX.Element}
 */
export const ClientApp = props => {
  const { store, hostedTranslations = {}, hostedConfig = {} } = props;
  const baseAppConfig = mergeConfig(hostedConfig, defaultConfig);
  const currentUserLanguage = getUserLanguagePreference(store.getState()?.user?.currentUser);
  const [preferredLanguage, setPreferredLanguage] = useState(
    () => readLanguageCookie(document.cookie) || currentUserLanguage
  );
  const pendingScrollPosition = useRef(null);
  const appConfig = localizedConfig(baseAppConfig, preferredLanguage);
  const messages = messagesForLanguage(appConfig.localization.language, hostedTranslations);
  const changeLanguage = useCallback(
    language => {
      const nextLanguage = resolveLanguage(language, baseAppConfig.localization.locale);
      if (nextLanguage === appConfig.localization.language) {
        return;
      }

      pendingScrollPosition.current = { left: window.scrollX, top: window.scrollY };
      storeLanguageCookie(nextLanguage);
      setPreferredLanguage(nextLanguage);
    },
    [appConfig.localization.language, baseAppConfig.localization.locale]
  );

  useLayoutEffect(() => {
    if (pendingScrollPosition.current) {
      window.scrollTo({ ...pendingScrollPosition.current, behavior: 'auto' });
      pendingScrollPosition.current = null;
    }
  }, [preferredLanguage]);

  useEffect(() => {
    // Clear referral data from session storage the expiration time has passed
    clearReferralDataIfExpired();

    // If URL contains new referral data, store it
    const urlReferralParams = parse(window?.location?.search);
    const { userTypes = [] } = appConfig.user;
    const isAuthenticated = store.getState()?.auth?.isAuthenticated;

    // Check if URL contains valid referral params.
    const validReferralParams = filterValidReferralData(urlReferralParams, userTypes);
    // Don't store referral data if the user is authenticated - meaning they have already completed
    // signup and have been redirected back here
    if (!isAuthenticated && !isEmpty(validReferralParams)) {
      storeReferralData(validReferralParams);
    }
  }, []);

  // Show warning on the localhost:3001, if the environment variable key contains "SECRET"
  if (appSettings.dev) {
    const envVars = process.env || {};
    const envVarKeys = Object.keys(envVars);
    const containsSECRET = str => str.toUpperCase().includes('SECRET');
    const suspiciousSECRETKey = envVarKeys.find(
      key => key.startsWith('REACT_APP_') && containsSECRET(key)
    );

    if (suspiciousSECRETKey) {
      return <EnvironmentVariableWarning suspiciousEnvKey={suspiciousSECRETKey} />;
    }
  }

  // Show MaintenanceMode if the mandatory configurations are not available
  if (!appConfig.hasMandatoryConfigurations) {
    return <MaintenanceModeError locale={appConfig.localization.locale} messages={messages} />;
  }

  // Marketplace color and the color for <PrimaryButton> come from configs
  // If set, we need to create CSS Property and set it to DOM (documentElement is selected here)
  // This provides marketplace color for everything under <html> tag (including modals/portals)
  // Note: This is also set on Page component to provide server-side rendering.
  const elem = window.document.documentElement;
  includeCSSProperties(appConfig.branding, elem);

  // This gives good input for debugging issues on live environments, but with test it's not needed.
  const logLoadDataCalls = appSettings?.env !== 'test';

  return (
    <LanguageProvider value={{ language: appConfig.localization.language, changeLanguage }}>
      <Configurations appConfig={appConfig}>
        <IntlProvider
          locale={appConfig.localization.locale}
          messages={messages}
          textComponent="span"
        >
          <Provider store={store}>
            <ConsumerIdentitySync />
            <LanguagePreferenceSync />
            <HelmetProvider>
              <IncludeScripts config={appConfig} initialPathname={window.location.pathname} />
              <BrowserRouter>
                <Routes logLoadDataCalls={logLoadDataCalls} />
              </BrowserRouter>
            </HelmetProvider>
          </Provider>
        </IntlProvider>
      </Configurations>
    </LanguageProvider>
  );
};

/**
 * Server App
 * @param {Object} props
 * @param {string} props.url
 * @param {Object} props.context
 * @param {Object} props.helmetContext
 * @param {Object} props.store
 * @param {Object} props.hostedTranslations
 * @param {Object} props.hostedConfig
 * @returns {JSX.Element}
 */
export const ServerApp = props => {
  const {
    url,
    context,
    helmetContext,
    store,
    preferredLanguage,
    hostedTranslations = {},
    hostedConfig = {},
  } = props;
  const baseAppConfig = mergeConfig(hostedConfig, defaultConfig);
  const currentUserLanguage = getUserLanguagePreference(store.getState()?.user?.currentUser);
  const appConfig = localizedConfig(baseAppConfig, preferredLanguage || currentUserLanguage);
  const messages = messagesForLanguage(appConfig.localization.language, hostedTranslations);
  HelmetProvider.canUseDOM = false;

  // Show MaintenanceMode if the mandatory configurations are not available
  if (!appConfig.hasMandatoryConfigurations) {
    return (
      <MaintenanceModeError
        locale={appConfig.localization.locale}
        messages={messages}
        helmetContext={helmetContext}
      />
    );
  }
  const initialPathname = new URL(url, 'http://example.com')?.pathname;

  return (
    <LanguageProvider
      value={{ language: appConfig.localization.language, changeLanguage: () => {} }}
    >
      <Configurations appConfig={appConfig}>
        <IntlProvider
          locale={appConfig.localization.locale}
          messages={messages}
          textComponent="span"
        >
          <Provider store={store}>
            <HelmetProvider context={helmetContext}>
              <IncludeScripts config={appConfig} initialPathname={initialPathname} />
              <StaticRouter location={url} context={context}>
                <Routes />
              </StaticRouter>
            </HelmetProvider>
          </Provider>
        </IntlProvider>
      </Configurations>
    </LanguageProvider>
  );
};

/**
 * Render the given route.
 *
 * @param {String} url Path to render
 * @param {Object} serverContext Server rendering context from react-router
 *
 * @returns {Object} Object with keys:
 *  - {String} body: Rendered application body of the given route
 *  - {Object} head: Application head metadata from react-helmet
 */
export const renderApp = (
  url,
  serverContext,
  preloadedState,
  hostedTranslations,
  hostedConfig,
  preferredLanguage,
  collectChunks
) => {
  // Don't pass an SDK instance since we're only rendering the
  // component tree with the preloaded store state and components
  // shouldn't do any SDK calls in the (server) rendering lifecycle.
  const store = configureStore({ initialState: preloadedState });

  const helmetContext = {};

  // When rendering the app on server, we wrap the app with webExtractor.collectChunks
  // This is needed to figure out correct chunks/scripts to be included to server-rendered page.
  // https://loadable-components.com/docs/server-side-rendering/#3-setup-chunkextractor-server-side
  const WithChunks = collectChunks(
    <ServerApp
      url={url}
      context={serverContext}
      helmetContext={helmetContext}
      store={store}
      preferredLanguage={preferredLanguage}
      hostedTranslations={hostedTranslations}
      hostedConfig={hostedConfig}
    />
  );

  // Let's keep react-dom/server out of the main code-chunk.
  return import('react-dom/server').then(mod => {
    const { default: ReactDOMServer } = mod;
    const body = ReactDOMServer.renderToString(WithChunks);
    const { helmet: head } = helmetContext;
    return { head, body };
  });
};
